import React, { useState } from 'react';
import { Users, Building, ArrowRight, CheckCircle, Star, Globe, Zap, Shield, TrendingUp, Play, ChevronRight, ChevronLeft, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import MainNavigation from './layout/MainNavigation';
import Footer from './layout/Footer';

const LandingPage = ({ onGetStarted }) => {
  const [activeTestimonialType, setActiveTestimonialType] = useState('organization');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeSection, setActiveSection] = useState('home'); // home, trainers, organizations

  const features = [
    {
      icon: <Globe className="w-8 h-8 text-blue-600" />,
      title: "Global Network",
      description: "Connect with trainers and organizations worldwide"
    },
    {
      icon: <Zap className="w-8 h-8 text-green-600" />,
      title: "Training Workflows",
      description: "Utilities for Trainers & Organizations for Training Workflows"
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-600" />,
      title: "Secure Platform",
      description: "Enterprise-grade security for all your training needs"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
      title: "Analytics & Insights",
      description: "Track performance and ROI with detailed analytics"
    }
  ];

  const stats = [
    { number: "18,000", label: "Trainers" },
    { number: "~100", label: "Organizations" },
    { number: "60,000+", label: "Mandays of Training" },
    { number: "~75", label: "Locations" }
  ];

  const testimonials = [
    {
      name: "Sneha S",
      role: "Training Manager",
      company: "Flipkart",
      type: "organization",
      rating: 5,
      text: "Our experiences with TrainerLinked.in has been seamless and easy. We get most of our training venue requirements fulfilled from them. They have a database of venues that are conveniently accessible by our employees."
    },
    {
      name: "Mona K",
      role: "HR Manager",
      company: "Heidelberg Cement",
      type: "organization",
      rating: 5,
      text: "TrainerLinked.in has been one of our partners from past 3 years in technical and functional training. They have excellent Pan India trainers who can facilitate training in many regional languages. The training is more experimental than theoretical."
    },
    {
      name: "Aditi Sharma",
      role: "Learning & Development",
      company: "Tata Projects",
      type: "organization",
      rating: 5,
      text: "Working with TrainerLinked.in has been quite satisfying in terms of quality of trainers provided, program content, commercials, professionalism and timeliness. We have continued sharing a close association from past many years."
    },
    {
      name: "Vivek Mehrotra",
      role: "Training Head",
      company: "Janalakshmi Financial Services",
      type: "organization",
      rating: 5,
      text: "Indeed, it was a pleasure working with TrainerLinked.in. Especially when we were ramping up our training operations across India. They provided much needed support in terms of identifying suitable training venues which met our requirements."
    },
    {
      name: "Gopichand Katamneni",
      role: "Director",
      company: "Integrated Clean Room Technologies",
      type: "organization",
      rating: 5,
      text: "We have witnessed a measurable positive change before and after the Training programs. The sense of Ownership demonstrated by the Team towards adding value is Amazing. Really Well Done meeting our expectations."
    },
    {
      name: "Vijay Y.V.C.S",
      role: "Program Manager",
      company: "TATA Trusts",
      type: "organization",
      rating: 5,
      text: "The platform has been instrumental in connecting us with quality trainers for our social impact programs across rural India. The trainer quality and program delivery has exceeded our expectations."
    },
    {
      name: "Dr. Abhishek Tiwari",
      role: "Trainer & Consultant",
      company: "Independent Training Professional", 
      type: "trainer",
      rating: 5,
      text: "I've been an associate for past 2 years. It has always been a pleasure to work for the clients. The best part is that they give free hands to trainers to decide the content based on client requirements. Backend support is always great."
    },
    {
      name: "Garima Goyal",
      role: "Trainer & Facilitator",
      company: "Corporate Training Specialist",
      type: "trainer",
      rating: 5,
      text: "Let me share my experience with TrainerLinked.in. It is one of the best platforms for training. They always respond to my queries on time and also let me customize the course as per client requirements. Overall very good experience."
    },
    {
      name: "Visshesh Prasaad",
      role: "Senior Trainer",
      company: "Leadership Development Expert",
      type: "trainer",
      rating: 5,
      text: "The platform has opened up incredible opportunities for me as a trainer. The quality of organizations I get to work with and the professional support provided by the team makes it a pleasure to be part of this network."
    },
    {
      name: "Priyanka Sharma",
      role: "Training Facilitator",
      company: "Behavioral Skills Trainer",
      type: "trainer",
      rating: 5,
      text: "TrainerLinked.in has transformed my training business. The platform connects me with reputed organizations and provides excellent support throughout the engagement process. Highly recommend for fellow trainers."
    },
    {
      name: "Rajesh Kumar",
      role: "Technical Trainer",
      company: "IT Training Specialist",
      type: "trainer",
      rating: 5,
      text: "Working through this platform has been seamless. The clients are professional, payments are timely, and the support team is always available to help. It's been a great partnership for my training practice."
    },
    {
      name: "Meera Nair",
      role: "Soft Skills Trainer",
      company: "Communication Expert",
      type: "trainer",
      rating: 5,
      text: "The platform provides excellent exposure to diverse client requirements across industries. I've been able to expand my training portfolio and work with some of the best organizations in the country."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <MainNavigation />

      {/* Hero Section - Contemporary Design */}
      <section className="relative overflow-hidden">
        {/* Background with organic shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-blue-400/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-1/2 right-0 w-96 h-96 bg-green-400/10 rounded-full translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-purple-400/10 rounded-full translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                <span className="text-sm font-medium text-gray-700">Live platform connecting training professionals</span>
              </div>
              
              <h1 className="hero-headline">
                <span className="block">Where</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-green-600">
                  Organizations
                </span>
                <span className="block">& Training Experts</span>
                <span className="block text-gray-600 italic" style={{fontSize: '42px', fontWeight: 400}}>connect</span>
              </h1>
              
              <p className="hero-subheadline mb-10">
                Break free from traditional training limitations. Our platform creates meaningful connections that transform learning experiences and drive measurable business impact.
              </p>

              {/* Action buttons with contemporary styling */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button
                  onClick={onGetStarted}
                  className="cta-button-large group relative bg-gray-900 text-white overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    I Need Training for My Organization
                    <Building className="w-5 h-5 ml-2 group-hover:rotate-12 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
                
                <button
                  onClick={onGetStarted}
                  className="cta-button-large group relative border-2 border-gray-900 text-gray-900 overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl bg-white"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    I'm a Training Professional
                    <Users className="w-5 h-5 ml-2 group-hover:rotate-12 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gray-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="absolute inset-0 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 cta-button-large">
                    I'm a Training Professional
                    <Users className="w-5 h-5 ml-2" />
                  </span>
                </button>
              </div>
            </div>

            {/* Right Content - Floating Stats */}
            <div className="lg:col-span-5">
              <div className="relative">
                {/* Floating stat cards with organic positioning */}
                <div className="space-y-6">
                  {stats.map((stat, index) => (
                    <div 
                      key={index} 
                      className={`relative bg-white/80 backdrop-blur-sm border border-gray-200 rounded-3xl p-6 shadow-xl transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${
                        index % 2 === 0 ? 'ml-0' : 'ml-8'
                      }`}
                      style={{
                        animationDelay: `${index * 200}ms`
                      }}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="stat-number">{stat.number}</div>
                        <div className="stat-label">{stat.label}</div>
                      </div>
                      {/* Decorative elements */}
                      <div className={`absolute -top-2 -right-2 w-4 h-4 rounded-full ${
                        index === 0 ? 'bg-blue-500' : 
                        index === 1 ? 'bg-green-500' : 
                        index === 2 ? 'bg-purple-500' : 'bg-orange-500'
                      }`}></div>
                    </div>
                  ))}
                </div>
                
                {/* Connecting lines between stats */}
                <svg className="absolute inset-0 w-full h-full -z-10" viewBox="0 0 300 400">
                  <path 
                    d="M50 80 Q150 120 250 160 T150 280 Q80 320 50 360" 
                    stroke="url(#gradient)" 
                    strokeWidth="2" 
                    fill="none" 
                    strokeDasharray="5,5"
                    opacity="0.3"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="50%" stopColor="#8B5CF6" />
                      <stop offset="100%" stopColor="#10B981" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Contemporary Asymmetric Layout */}
      <section id="features" className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="grid lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-5">
              <div>
                <div className="section-label">Why Choose</div>
                <div className="section-headline">TrainerLinked.in?</div>
              </div>
            </div>
            <div className="lg:col-span-7 flex items-center">
              <p className="section-description">
                TrainerLinked.in is a powerful, end-to-end platform designed for the entire corporate training ecosystem. Whether you're a trainer looking for opportunities or a platform to network; or an organization seeking the right trainers and streamlining training-related process automation, our platform removes friction, helps discovery, improves coordination, and maximizes the efficiency of the entire corporate training operations.
              </p>
            </div>
          </div>

          {/* Features in Organic Layout */}
          <div className="relative">
            {/* Large feature - Top Left */}
            <div className="grid lg:grid-cols-2 gap-12 mb-16">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-[3rem] p-10 relative overflow-hidden group hover:scale-105 transition-transform duration-500">
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform duration-300">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>{features[0].title}</h3>
                  <p className="text-gray-700" style={{fontFamily: 'Inter, sans-serif', fontSize: '19px', lineHeight: '1.6'}}>{features[0].description}</p>
                </div>
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-400/20 rounded-full"></div>
              </div>

              <div className="space-y-8">
                {/* Smaller feature cards */}
                <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-green-200 hover:shadow-xl transition-all duration-300 group">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>{features[1].title}</h3>
                      <p className="text-gray-600" style={{fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6'}}>{features[1].description}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-purple-200 hover:shadow-xl transition-all duration-300 group">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>{features[2].title}</h3>
                      <p className="text-gray-600" style={{fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6'}}>{features[2].description}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom feature - Full width */}
            <div className="bg-gradient-to-r from-orange-50 via-red-50 to-pink-50 rounded-[3rem] p-10 relative overflow-hidden group hover:scale-[1.02] transition-transform duration-500">
              <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
                <div className="w-20 h-20 bg-orange-600 rounded-3xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                  <TrendingUp className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>{features[3].title}</h3>
                  <p className="text-gray-700 max-w-2xl" style={{fontFamily: 'Inter, sans-serif', fontSize: '19px', lineHeight: '1.6'}}>{features[3].description}</p>
                </div>
              </div>
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-orange-400/20 rounded-full"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-pink-400/20 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Simple steps to get started</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Create Your Profile</h3>
              <p className="text-gray-600">Sign up and create a comprehensive profile showcasing your expertise or requirements</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Connect & Match</h3>
              <p className="text-gray-600">Our AI-powered system matches you with the perfect training partners</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Deliver Excellence</h3>
              <p className="text-gray-600">Manage your projects, track progress, and deliver exceptional training experiences</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 relative overflow-hidden bg-gray-900">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-64 h-64 bg-blue-600/10 rounded-full -translate-x-1/2"></div>
          <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-green-600/10 rounded-full translate-x-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-white mb-6">
              What Our Users 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-green-400">
                Actually Say
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Real feedback from training professionals and organizations who've transformed their approach to corporate learning
            </p>
          </div>

          {/* Testimonial Type Selector */}
          <div className="flex justify-center mb-12">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-2 flex space-x-2">
              <button
                onClick={() => {
                  setActiveTestimonialType('organization');
                  setCurrentSlide(0);
                }}
                className={`flex items-center space-x-3 px-8 py-4 rounded-xl font-bold transition-all duration-300 ${
                  activeTestimonialType === 'organization'
                    ? 'bg-blue-500 text-white shadow-2xl scale-105'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                <Building className="w-6 h-6" />
                <span className="text-lg">Organizations</span>
              </button>
              <button
                onClick={() => {
                  setActiveTestimonialType('trainer');
                  setCurrentSlide(0);
                }}
                className={`flex items-center space-x-3 px-8 py-4 rounded-xl font-bold transition-all duration-300 ${
                  activeTestimonialType === 'trainer'
                    ? 'bg-green-500 text-white shadow-2xl scale-105'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                <Users className="w-6 h-6" />
                <span className="text-lg">Trainers</span>
              </button>
            </div>
          </div>

          {/* Testimonials Carousel */}
          <div className="relative">
            {(() => {
              const filteredTestimonials = testimonials.filter(t => t.type === activeTestimonialType);
              const testimonialsToShow = filteredTestimonials.slice(currentSlide, currentSlide + 3);
              const isOrganization = activeTestimonialType === 'organization';
              
              return (
                <>
                  <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8 min-h-[350px]">
                    {testimonialsToShow.map((testimonial, index) => (
                      <div 
                        key={`${testimonial.name}-${index}`} 
                        className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-2 hover:scale-105"
                      >
                        {/* Quote mark */}
                        <div className={`absolute -top-4 -left-4 w-12 h-12 ${
                          isOrganization ? 'bg-blue-500' : 'bg-green-500'
                        } rounded-2xl flex items-center justify-center rotate-12`}>
                          <span className="text-white text-2xl font-bold">"</span>
                        </div>
                        
                        {/* Stars */}
                        <div className="flex items-center mb-6">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                          ))}
                        </div>
                        
                        {/* Content */}
                        <p className="text-gray-100 mb-6 leading-relaxed font-medium">
                          {testimonial.text}
                        </p>
                        
                        {/* Author */}
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 ${
                            isOrganization ? 'bg-blue-500' : 'bg-green-500'
                          } rounded-2xl flex items-center justify-center`}>
                            {isOrganization ? (
                              <Building className="w-6 h-6 text-white" />
                            ) : (
                              <Users className="w-6 h-6 text-white" />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-white">{testimonial.name}</p>
                            <p className="text-gray-300 text-sm">{testimonial.role}</p>
                            <p className={`text-sm font-medium ${
                              isOrganization ? 'text-blue-300' : 'text-green-300'
                            }`}>
                              {testimonial.company}
                            </p>
                          </div>
                        </div>
                        
                        {/* Decorative elements */}
                        <div className={`absolute -bottom-2 -right-2 w-8 h-8 ${
                          isOrganization ? 'bg-blue-400/30' : 'bg-green-400/30'
                        } rounded-full`}></div>
                      </div>
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  {filteredTestimonials.length > 3 && (
                    <div className="flex justify-center items-center mt-8 space-x-4">
                      <button
                        onClick={() => setCurrentSlide(Math.max(0, currentSlide - 3))}
                        disabled={currentSlide === 0}
                        className={`p-2 rounded-full transition-all ${
                          currentSlide === 0
                            ? 'text-gray-400 cursor-not-allowed'
                            : isOrganization
                              ? 'text-blue-600 hover:bg-blue-100'
                              : 'text-green-600 hover:bg-green-100'
                        }`}
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      
                      {/* Dots Indicator */}
                      <div className="flex space-x-2">
                        {Array.from({ length: Math.ceil(filteredTestimonials.length / 3) }).map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentSlide(index * 3)}
                            className={`w-3 h-3 rounded-full transition-all ${
                              Math.floor(currentSlide / 3) === index
                                ? isOrganization ? 'bg-blue-600' : 'bg-green-600'
                                : 'bg-gray-300 hover:bg-gray-400'
                            }`}
                          />
                        ))}
                      </div>

                      <button
                        onClick={() => setCurrentSlide(Math.min(filteredTestimonials.length - 3, currentSlide + 3))}
                        disabled={currentSlide + 3 >= filteredTestimonials.length}
                        className={`p-2 rounded-full transition-all ${
                          currentSlide + 3 >= filteredTestimonials.length
                            ? 'text-gray-400 cursor-not-allowed'
                            : isOrganization
                              ? 'text-blue-600 hover:bg-blue-100'
                              : 'text-green-600 hover:bg-green-100'
                        }`}
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Transform Your Training Experience?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join the leading platform connecting world-class trainers with forward-thinking organizations
          </p>
          <Button 
            onClick={onGetStarted}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
          >
            Get Started Today
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">TrainerLinked.in</span>
              </div>
              <p className="text-gray-400">
                Connecting training professionals with organizations worldwide.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">For Trainers</a></li>
                <li><a href="#" className="hover:text-white">For Organizations</a></li>
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Contact Us</a></li>
                <li><a href="#" className="hover:text-white">Community</a></li>
                <li><a href="#" className="hover:text-white">Resources</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">About Us</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 TrainerLinked.in. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <Footer />
    </div>
  );
};

export default LandingPage;